"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { 
  PiFacebookLogo, 
  PiInstagramLogo, 
  PiLinkedinLogo, 
  PiYoutubeLogo,
  PiArrowUpRight
} from "react-icons/pi";
import { getImageUrl } from "@/utils/image-url";

interface UniversityFooterProps extends SectionContentProps {
  content: any;
}

export const UniversityFooter: React.FC<UniversityFooterProps> = ({
  content: propContent,
  onContentChange,
  isSelected,
  defaultLogo,
  defaultSiteName,
}) => {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Dynamic colors with fallbacks (dark themed Instacks look)
  const backgroundColor = content.backgroundColor || "#121212"; 
  const textColor = content.textColor || "#A3A3A3"; // neutral-400
  const primaryColor = content.colorPalette?.primary || "#7A1520"; // Instacks Crimson/Maroon

  // Handle updates to content fields helper
  const updateField = (key: string, value: any) => {
    if (onContentChange) {
      onContentChange({
        ...content,
        [key]: value,
      });
    }
  };

  // Nav columns
  const col1Title = content.col1Title || "Our Campus";
  const col1Links = content.col1Links || [
    { label: "Academic", href: "#" },
    { label: "Athletics", href: "#" },
    { label: "Campus life", href: "#" },
    { label: "Research", href: "#" },
    { label: "Academic Area", href: "#" }
  ];

  const col2Title = content.col2Title || "Our Campus";
  const col2Links = content.col2Links || [
    { label: "About", href: "#" },
    { label: "Tution Fee", href: "#" },
    { label: "Alumni", href: "#" },
    { label: "Faculty Staff", href: "#" },
    { label: "Event", href: "#" }
  ];

  const handleLinkChange = (colIndex: 1 | 2, linkIndex: number, label: string) => {
    const fieldName = colIndex === 1 ? "col1Links" : "col2Links";
    const newLinks = [...(colIndex === 1 ? col1Links : col2Links)];
    newLinks[linkIndex] = { ...newLinks[linkIndex], label };
    updateField(fieldName, newLinks);
  };

  return (
    <footer
      className={`relative pt-16 pb-8 border-t border-white/5 ${isSelected ? "ring-2 ring-blue-500 z-10" : ""}`}
      style={{
        fontFamily,
        backgroundColor,
      }}
      role="contentinfo"
      aria-label="University footer"
    >
      {/* Main Footer Container */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* Top 4-Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 pb-16">
          
          {/* Logo & Brand Column */}
          <div className="lg:col-span-4 flex flex-col items-start space-y-6">
            
            {/* SVG Logo Emblem + Text OR Image Logo */}
            {(() => {
              const rawLogo = content.logo || defaultLogo;
              const logoSrc = rawLogo ? (getImageUrl(rawLogo) || rawLogo) : null;
              return logoSrc ? (
                <div className="flex items-center gap-3">
                  <img
                    src={logoSrc}
                    alt="Logo"
                    className="h-10 max-h-12 w-auto object-contain"
                    onError={(e) => {
                      // If doubled path occurs or image fails to load, fallback to raw or clean relative path
                      const target = e.target as HTMLImageElement;
                      if (rawLogo && rawLogo.includes("//")) {
                        const cleaned = rawLogo.replace(/(?<!:)\/\/+/g, "/");
                        if (target.src !== cleaned) {
                          target.src = cleaned;
                          return;
                        }
                      }
                      target.style.display = "none";
                    }}
                  />
                </div>
              ) : (
              <div className="flex items-center gap-3">
                {/* Laurel Wreath Crest SVG */}
                <div className="w-12 h-12 flex-shrink-0 text-white fill-current">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    {/* Outer Laurel Leaves Wreath */}
                    <path d="M25,50 C23,40 28,30 35,25 C30,32 30,42 32,50 C33,55 35,60 38,65 C33,60 27,55 25,50 Z" opacity="0.85" />
                    <path d="M75,50 C77,40 72,30 65,25 C70,32 70,42 68,50 C67,55 65,60 62,65 C67,60 73,55 75,50 Z" opacity="0.85" />
                    <path d="M30,35 C28,28 32,22 38,18 C34,24 34,31 35,38 Z" opacity="0.85" />
                    <path d="M70,35 C72,28 68,22 62,18 C66,24 66,31 65,38 Z" opacity="0.85" />
                    {/* Wreath bottom ribbon */}
                    <path d="M42,75 C45,78 48,80 50,80 C52,80 55,78 58,75 C54,76 46,76 42,75 Z" />
                    
                    {/* Center Crest Shield Outline */}
                    <path d="M50,22 C59,22 64,26 64,26 C64,26 64,52 50,68 C36,52 36,26 36,26 C36,26 41,22 50,22 Z" fill="none" stroke="currentColor" strokeWidth="2.5" />
                    
                    {/* Inside Shield: Graduation Cap */}
                    <polygon points="50,29 60,34 50,39 40,34" />
                    <path d="M43,36.5 L43,45 C43,48 57,48 57,45 L57,36.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <line x1="60" y1="34" x2="60" y2="43" stroke="currentColor" strokeWidth="1" />
                    <circle cx="60" cy="43" r="1.5" />
                    
                    {/* Inside Shield: Star */}
                    <polygon points="50,49 52,53 56,53 53,55 54,59 50,57 46,59 47,55 44,53 48,53" />
                  </svg>
                </div>

                {/* Text: Instacks University */}
                <div className="flex flex-col text-white font-serif leading-none tracking-wider">
                  <span className="text-xl font-bold font-sans">INSTACKS</span>
                  <span className="text-[10px] tracking-[0.25em] font-sans font-light text-white/70 mt-0.5">UNIVERSITY</span>
                </div>
              </div>
              );
            })()}

            {/* Description Text */}
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              <EditableText
                value={content.description || "We are passionate education dedicated to providing high-quality resources learners all backgrounds."}
                onChange={(val) => updateField("description", val)}
                as="span"
                placeholder="Footer description text..."
              />
            </p>

            {/* Social Icons (Outline Circles) */}
            <div className="flex items-center gap-3">
              {[
                { icon: <PiFacebookLogo className="w-5 h-5" />, key: "facebookUrl", defaultVal: "#" },
                { icon: <PiInstagramLogo className="w-5 h-5" />, key: "instagramUrl", defaultVal: "#" },
                { icon: <PiLinkedinLogo className="w-5 h-5" />, key: "linkedinUrl", defaultVal: "#" },
                { icon: <PiYoutubeLogo className="w-5 h-5" />, key: "youtubeUrl", defaultVal: "#" }
              ].map((item, idx) => (
                <a
                  key={idx}
                  href={content[item.key] || item.defaultVal}
                  className="w-9 h-9 rounded-full border border-white/20 hover:border-white/50 flex items-center justify-center text-white hover:text-white transition-all bg-transparent hover:bg-white/5"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.icon}
                </a>
              ))}
            </div>

          </div>

          {/* Spacer for wider screens */}
          <div className="hidden lg:block lg:col-span-1" />

          {/* Navigation Column 1 */}
          <div className="lg:col-span-2 flex flex-col space-y-4">
            <h4 className="text-white font-semibold text-base tracking-wide">
              <EditableText
                value={col1Title}
                onChange={(val) => updateField("col1Title", val)}
                as="span"
                placeholder="Column 1 Title"
              />
            </h4>
            <ul className="space-y-3" role="list">
              {col1Links.map((link: any, idx: number) => (
                <li key={idx}>
                  <a
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors block"
                  >
                    <EditableText
                      value={link.label}
                      onChange={(val) => handleLinkChange(1, idx, val)}
                      as="span"
                      placeholder="Link label"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation Column 2 */}
          <div className="lg:col-span-2 flex flex-col space-y-4">
            <h4 className="text-white font-semibold text-base tracking-wide">
              <EditableText
                value={col2Title}
                onChange={(val) => updateField("col2Title", val)}
                as="span"
                placeholder="Column 2 Title"
              />
            </h4>
            <ul className="space-y-3" role="list">
              {col2Links.map((link: any, idx: number) => (
                <li key={idx}>
                  <a
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors block"
                  >
                    <EditableText
                      value={link.label}
                      onChange={(val) => handleLinkChange(2, idx, val)}
                      as="span"
                      placeholder="Link label"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Buttons Column */}
          <div className="lg:col-span-3 flex flex-col space-y-4">
            <h4 className="text-white font-semibold text-base tracking-wide">
              <EditableText
                value={content.ctaTitle || "Quick Button"}
                onChange={(val) => updateField("ctaTitle", val)}
                as="span"
                placeholder="CTA Column Title"
              />
            </h4>
            <div className="flex flex-col space-y-3 max-w-[240px]">
              {/* Primary Maroon Action Button */}
              <a
                href={content.primaryCtaLink || "#"}
                className="w-full py-3.5 px-6 rounded-full text-white font-medium text-sm text-center transition-all hover:brightness-110 flex items-center justify-center gap-2"
                style={{ backgroundColor: primaryColor }}
              >
                <EditableText
                  value={content.primaryCtaText || "Applying"}
                  onChange={(val) => updateField("primaryCtaText", val)}
                  as="span"
                  placeholder="Primary CTA"
                />
              </a>

              {/* Secondary Transparent Action Button */}
              <a
                href={content.secondaryCtaLink || "#"}
                className="w-full py-3.5 px-6 rounded-full border border-white/20 hover:border-white/50 text-white font-medium text-sm text-center transition-all bg-transparent hover:bg-white/5 flex items-center justify-center gap-2"
              >
                <EditableText
                  value={content.secondaryCtaText || "Scholarship"}
                  onChange={(val) => updateField("secondaryCtaText", val)}
                  as="span"
                  placeholder="Secondary CTA"
                />
              </a>
            </div>
          </div>

        </div>

        {/* Bottom copyright/legal area */}
        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-center text-center">
          <p className="text-white/40 text-xs">
            Copyright © {new Date().getFullYear()} All Rights Reserved by{" "}
            <span style={{ color: primaryColor }} className="font-semibold transition-colors">
              <EditableText
                value={content.copyrightBrand || defaultSiteName || "Instacks"}
                onChange={(val) => updateField("copyrightBrand", val)}
                as="span"
                placeholder="Brand Name"
              />
            </span>
          </p>
        </div>

      </div>
    </footer>
  );
};

export default UniversityFooter;
